import type { CSSProperties } from 'react';
import { Card, theme, Typography } from 'antd';
import { twMerge } from 'tailwind-merge';
import useIntersectionObserver from '@/hooks/useIntersectionObserver';

type CardShellProps = {
  children: React.ReactNode;
  className?: string;
  style?: CSSProperties;
  bodyStyle?: CSSProperties;
  testId?: string;
  title?: string | React.ReactNode;
  headerOptions?: React.ReactNode;
  type?: 'default' | 'secondary';
  animation?: {
    enabled?: boolean;
    delay?: number;
  };
};

const CardShellEcho = ({
  children,
  className,
  style,
  bodyStyle,
  testId,
  title,
  headerOptions,
  type = 'default',
  animation = {
    enabled: true,
    delay: 0,
  },
}: CardShellProps) => {
  const { token } = theme.useToken();

  const prefersReducedMotion =
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const motionEnabled = !!animation.enabled && !prefersReducedMotion;

  const { isIntersecting, ref } = useIntersectionObserver({
    threshold: 0.1,
    rootMargin: '-50px',
    triggerOnce: true,
    enabled: motionEnabled,
  });

  const motionRootStyle = motionEnabled
    ? {
        transition: 'opacity 1000ms ease-out, transform 1000ms ease-out',
        transitionDelay: isIntersecting ? `${animation.delay}ms` : '0ms',
        opacity: isIntersecting ? 1 : 0,
        transform: isIntersecting ? 'translateY(0)' : `translateY(${token.marginXL}px)`,
      }
    : {};

  const cardTitle =
    title != null && title !== '' ? (
      type === 'secondary' ? (
        <Typography.Title level={5} type="secondary" style={{ margin: 0 }}>
          {title}
        </Typography.Title>
      ) : (
        <Typography.Title level={3} style={{ margin: 0 }}>
          {title}
        </Typography.Title>
      )
    ) : undefined;

  return (
    <Card
      ref={ref}
      data-testid={testId}
      variant="outlined"
      title={cardTitle}
      extra={headerOptions ? headerOptions : undefined}
      className={twMerge(className)}
      styles={{
        root: {
          ...motionRootStyle,
          display: 'flex',
          flexDirection: 'column',
          background: token.colorBgContainer,
          borderColor: token.colorBorderSecondary,
          ...(style ?? {}),
        },
        body: {
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          padding: token.paddingLG,
          ...bodyStyle,
        },
      }}
    >
      {children}
    </Card>
  );
};

export default CardShellEcho;
